// The Compendium's default view, the groups that come from the table itself (issue 1585, slice 2): each a pure function
// of what THIS viewer's store and log already hold — the DM's state is whole, a player's is their projection — so no
// group can show a player anything they were not sent. And the two the DM's browser remembers (`localStorage`, per
// person and campaign, every read and write tried): what the Loot & shops tool last showed on a scene, and the entries
// the DM last opened from the Compendium.
import type { DmObject, PlayerObject } from '@dragons-table/vtt-shared';
import type { LogEntry } from './dice';
import type { ConditionEntry } from './conditions';

/**
 * One row a table group shows: the entry it opens (`segment` and `slug`, absent when there is none to open — a condition
 * the site has no page for, an entry a player was sent by its name alone), its family's label, a right-hand
 * detail, and whether any token it stands for is hidden (the DM's alone).
 */
export interface TableRow { name: string; segment?: string; slug?: string; family?: string; detail?: string; hidden?: true }
type Objects = Record<string, PlayerObject | DmObject>;
/** A family's label by its segment, from the compendium's families list. */
export type FamilyLabels = ReadonlyMap<string, string>;

/** A whole entry reference, which only the DM's state holds; a player's `entry` is `{ name }` and never matches. */
const whole = (e: unknown): e is { segment: string; slug: string; name: string } => {
  const o = e as Record<string, unknown> | undefined;
  return !!o && typeof o.segment === 'string' && typeof o.slug === 'string' && typeof o.name === 'string';
};
const withAddress = (segment: string, slug: string, families: FamilyLabels) => ({ segment, slug, ...(families.get(segment) ? { family: families.get(segment)! } : {}) });

/** "On this scene" (the DM): the entries the scene's tokens carry, one row each, "×3" for more than one, hidden flagged. */
export function onThisScene(objects: Objects, families: FamilyLabels): TableRow[] {
  const by = new Map<string, { name: string; segment: string; slug: string; n: number; hidden: boolean }>();
  for (const o of Object.values(objects)) {
    const e = (o as DmObject).entry;
    // The DM's whole reference (its system too): a player's store holds a told creature's address alone (issue 1585).
    if (!whole(e) || typeof (e as { system?: unknown }).system !== 'string') continue;
    const key = `${e.segment}/${e.slug}`;
    const was = by.get(key) ?? { name: e.name, segment: e.segment, slug: e.slug, n: 0, hidden: false };
    was.n += 1;
    if ((o as DmObject).hidden === true) was.hidden = true;
    by.set(key, was);
  }
  return [...by.values()].sort((a, b) => a.name.localeCompare(b.name)).map((g) => ({
    name: g.name, ...withAddress(g.segment, g.slug, families), ...(g.n > 1 ? { detail: `×${g.n}` } : {}), ...(g.hidden ? { hidden: true as const } : {}),
  }));
}

/** How long ago, in words: "just now", "5 min ago", "2 h ago", "3 d ago". */
export function ago(ms: number): string {
  const min = Math.floor(Math.max(0, ms) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  return h < 24 ? `${h} h ago` : `${Math.floor(h / 24)} d ago`;
}

/** One cast as this viewer's log holds it: the DM's record whole, a player's as they were sent it. */
function castOf(e: LogEntry): { name: string; ref?: string; caster: string | null; at: string } | null {
  if (e.kind === 'roll') {
    const s = e.roll.attack?.spell;
    if (!s) return null;
    return { name: s.spell.name, ...(s.spell.ref ? { ref: s.spell.ref } : {}), caster: e.roll.attack!.attacker.name, at: e.roll.at };
  }
  if (e.kind === 'attack') {
    const s = e.attack.spell;
    // A private cast tells a player no name (issue 1585's projection): it is not a row.
    if (!s?.name) return null;
    return { name: s.name, ...(s.ref ? { ref: s.ref } : {}), caster: e.attack.attacker ? (e.attack.attacker.name ?? 'A creature') : null, at: e.attack.at };
  }
  return null;
}

/** "Recently cast" (both): the spells in the log this viewer holds, newest first, one row each (its latest cast). `now` is the server's clock. */
export function recentlyCast(log: readonly LogEntry[], now: number, families: FamilyLabels): TableRow[] {
  const casts = log.map(castOf).filter((c): c is NonNullable<ReturnType<typeof castOf>> => c !== null)
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  const seen = new Set<string>();
  const out: TableRow[] = [];
  for (const c of casts) {
    const key = c.ref ?? `name:${c.name.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const [segment, slug] = c.ref ? c.ref.split('/') as [string, string] : [];
    const when = ago(now - Date.parse(c.at));
    out.push({ name: c.name, ...(segment && slug ? withAddress(segment, slug, families) : {}), detail: c.caster ? `${c.caster} · ${when}` : when });
  }
  return out;
}

/** "Alice, Bob, Cat +2": at most three names. */
const whoHas = (names: string[]): string => (names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', '));

function conditionRows(objects: PlayerObject[] | DmObject[], entries: readonly ConditionEntry[], families: FamilyLabels, withWho: boolean): TableRow[] {
  const known = new Map(entries.map((c) => [c.name.toLowerCase(), c]));
  const by = new Map<string, { name: string; who: string[] }>();
  for (const o of objects) {
    for (const raw of o.values.conditions ?? []) {
      const name = raw.trim();
      if (!name) continue;
      const g = by.get(name.toLowerCase()) ?? { name, who: [] };
      const who = (o as { name?: string }).name;
      if (who && !g.who.includes(who)) g.who.push(who);
      by.set(name.toLowerCase(), g);
    }
  }
  return [...by.values()].sort((a, b) => a.name.localeCompare(b.name)).map((g) => {
    const e = known.get(g.name.toLowerCase());
    return { name: g.name, ...(e ? withAddress(e.segment, e.slug, families) : {}), ...(withWho && g.who.length ? { detail: whoHas(g.who) } : {}) };
  });
}

/** "Conditions in play" (the DM): every condition on the scene's tokens, with who has it. */
export function conditionsInPlay(objects: Objects, entries: readonly ConditionEntry[], families: FamilyLabels): TableRow[] {
  return conditionRows(Object.values(objects) as DmObject[], entries, families, true);
}

/** "On you" (a player): the conditions on their own tokens on this scene. */
export function onYou(objects: Objects, meId: string, entries: readonly ConditionEntry[], families: FamilyLabels): TableRow[] {
  const mine = Object.values(objects).filter((o) => o.owners.includes(meId)) as PlayerObject[];
  return conditionRows(mine, entries, families, mine.length > 1);
}

/**
 * "Revealed by the DM" (a player): another's tokens whose entry they were sent and whose name the DM told them in
 * Properties (issue 1575). An untold token — an "Unknown creature" — is never a row, whatever its entry. Under the
 * condition a player is sent an entry's name, they are sent its address too (slice 3), so a row opens the entry.
 */
export function revealedByDm(objects: Objects, meId: string, families: FamilyLabels): TableRow[] {
  // Slice 3: the told name now brings the entry's address (`projectObject`), so the row is the entry's; one sent by its
  // name alone stays plain text. One row per entry (by address, else by name).
  const by = new Map<string, TableRow>();
  for (const o of Object.values(objects)) {
    if (o.owners.includes(meId) || !o.told?.includes('name')) continue;
    const e = o.entry as { name?: unknown; segment?: unknown; slug?: unknown } | undefined;
    const name = typeof e?.name === 'string' ? e.name.trim() : '';
    if (!name) continue;
    const at = typeof e!.segment === 'string' && typeof e!.slug === 'string' ? { segment: e!.segment, slug: e!.slug } : null;
    const key = at ? `@${at.segment}/${at.slug}` : name.toLowerCase();
    if (!by.has(key)) by.set(key, at ? { name, ...withAddress(at.segment, at.slug, families) } : { name });
  }
  return [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
}

// ── What the DM's browser remembers ─────────────────────────────────────────

const LOOT_MAX = 50;
const OPENED_MAX = 20;
interface Remembered { name: string; segment: string; slug: string; family?: string; detail?: string }
const isRemembered = (v: unknown): v is Remembered => {
  const o = v as Record<string, unknown> | null;
  return !!o && typeof o.name === 'string' && typeof o.segment === 'string' && typeof o.slug === 'string'
    && (o.family === undefined || typeof o.family === 'string') && (o.detail === undefined || typeof o.detail === 'string');
};
function read(key: string): Remembered[] {
  try {
    const v = JSON.parse(window.localStorage.getItem(key) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter(isRemembered) : [];
  } catch { return []; }
}
function write(key: string, rows: Remembered[]): void {
  try { window.localStorage.setItem(key, JSON.stringify(rows)); } catch { /* not remembered */ }
}

const lootKey = (userId: string, campaignId: string, sceneId: string | null) => `tdt-vtt-compendium-loot:${userId}:${campaignId}:${sceneId ?? 'none'}`;
/** The Loot & shops tool showed a result on this scene: its items with an entry of their own, under the source's words. */
export function rememberLoot(userId: string, campaignId: string, sceneId: string | null, source: string, items: ReadonlyArray<{ segment: string; slug: string; name: string; made: boolean }>): void {
  const seen = new Set<string>();
  const rows: Remembered[] = [];
  for (const i of items) {
    const key = `${i.segment}/${i.slug}`;
    if (i.made || seen.has(key)) continue;
    seen.add(key);
    rows.push({ name: i.name, segment: i.segment, slug: i.slug, detail: source });
    if (rows.length >= LOOT_MAX) break;
  }
  write(lootKey(userId, campaignId, sceneId), rows);
}
/** "Might find here" (the DM): what the Loot & shops tool last showed on this scene. */
export function mightFindHere(userId: string, campaignId: string, sceneId: string | null, families: FamilyLabels): TableRow[] {
  return read(lootKey(userId, campaignId, sceneId)).map((r) => ({ name: r.name, ...withAddress(r.segment, r.slug, families), ...(r.detail ? { detail: r.detail } : {}) }));
}

const openedKey = (userId: string, campaignId: string) => `tdt-vtt-compendium-opened:${userId}:${campaignId}`;
/** The DM opened an entry from the Compendium: it goes first, once, at most twenty kept. */
export function rememberOpened(userId: string, campaignId: string, e: { name: string; segment: string; slug: string; family?: string }): void {
  const rest = read(openedKey(userId, campaignId)).filter((r) => r.segment !== e.segment || r.slug !== e.slug);
  write(openedKey(userId, campaignId), [{ name: e.name, segment: e.segment, slug: e.slug, ...(e.family ? { family: e.family } : {}) }, ...rest].slice(0, OPENED_MAX));
}
/** "Recently opened" (the DM): newest first. */
export function recentlyOpened(userId: string, campaignId: string, families: FamilyLabels): TableRow[] {
  return read(openedKey(userId, campaignId)).map((r) => ({ name: r.name, segment: r.segment, slug: r.slug, ...((families.get(r.segment) ?? r.family) ? { family: families.get(r.segment) ?? r.family! } : {}) }));
}
